import * as React from 'react';
import type { SelectProps } from 'antd';
import { Select, Spin } from 'antd';
import debounce from 'lodash/debounce';

/** How many options one page of a PaginatedSelect asks the API for. */
export const PAGE_SIZE = 20;

export interface PaginatedOption {
    value: string | number;
    label: React.ReactNode;
}

export interface PaginatedPage {
    options: PaginatedOption[];
    /** whether another page exists after this one */
    more: boolean;
}

export interface PaginatedSelectProps
    extends Omit<SelectProps<any>, 'options' | 'children' | 'onSearch' | 'filterOption' | 'onPopupScroll'> {
    /** One page of options for a search; the component asks for the next page
        as the list is scrolled to its end. */
    fetchPage: (search: string, page: number) => Promise<PaginatedPage>;
    /** Options shown first whatever the search or page: the current value, an
        "all" entry. Duplicates from the fetched pages are dropped. */
    pinned?: PaginatedOption[];
    debounceTimeout?: number;
}

/**
 * A searchable select whose options arrive one page at a time.
 *
 * The API holds more rows than a dropdown should load at once, so the list is
 * fetched page by page: the first page when the control mounts, the next as
 * the popup is scrolled to the bottom, and page one again for each search.
 */
const PaginatedSelect = ({ fetchPage, pinned = [], debounceTimeout = 400, ...selectProps }: PaginatedSelectProps) => {
    const [options, setOptions] = React.useState<PaginatedOption[]>([]);
    const [search, setSearch] = React.useState('');
    const [page, setPage] = React.useState(1);
    const [more, setMore] = React.useState(true);
    const [loading, setLoading] = React.useState(false);
    // the latest fetcher, so a caller need not memoise it and no fetch is
    // repeated because a parent rendered
    const fetchRef = React.useRef(fetchPage);
    fetchRef.current = fetchPage;
    // a slow response to an earlier search or page must not overwrite a later one
    const requestRef = React.useRef(0);

    const load = React.useCallback((nextSearch: string, nextPage: number) => {
        const requestId = ++requestRef.current;
        setLoading(true);
        fetchRef
            .current(nextSearch, nextPage)
            .then(({ options: fetched, more: hasMore }) => {
                if (requestId !== requestRef.current) return;
                setOptions((current) => (nextPage === 1 ? fetched : [...current, ...fetched]));
                setMore(hasMore);
                setPage(nextPage);
            })
            .catch(() => {
                if (requestId === requestRef.current) setMore(false);
            })
            .finally(() => {
                if (requestId === requestRef.current) setLoading(false);
            });
    }, []);

    React.useEffect(() => {
        load('', 1);
    }, [load]);

    const onSearch = React.useMemo(
        () =>
            debounce((value: string) => {
                setSearch(value);
                load(value, 1);
            }, debounceTimeout),
        [load, debounceTimeout],
    );

    const onPopupScroll = (event: React.UIEvent<HTMLDivElement>) => {
        const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
        const nearBottom = scrollTop + clientHeight >= scrollHeight - 24;
        if (nearBottom && more && !loading) load(search, page + 1);
    };

    const pinnedValues = new Set(pinned.map((option) => option.value));
    const shown = [...pinned, ...options.filter((option) => !pinnedValues.has(option.value))];

    return (
        <Select
            {...selectProps}
            showSearch
            filterOption={false}
            options={shown}
            loading={loading || selectProps.loading}
            onSearch={onSearch}
            onPopupScroll={onPopupScroll}
            notFoundContent={loading ? <Spin size="small" /> : 'No results'}
        />
    );
};

export default PaginatedSelect;
